import { woffToSfnt } from "@fg/web-core/pdf";
import { FONT_CDN, isStandardFont as themeIsStandard, localFontRecords, onlineFont, showFont as themeShowFont, type LocalFontData, type OnlineFont } from "@fg/ui-theme";
import { BUNDLED_FAMILIES, isBundledFamily, loadBundled, loadGoogle, showBundled } from "./fontres";

// The font catalog (standard, online open-licence, installed) lives in @fg/ui-theme so every editor offers the same fonts.
// This file adds what only Fowlguard PDF needs: the TrueType program of a chosen font, to embed in the saved file.
export {
    COMMON_FONTS, cssFamily, cssFont, FONT_CDN, loadLocalFonts, localFamilies, localFontsSupported, onLocalFonts, ONLINE_FONTS, STANDARD_FONTS
} from "@fg/ui-theme";
export { BUNDLED_FAMILIES, isBundledFamily };

/** Families whose program was put in by the Reader itself (a font taken from the document, or downloaded). */
const registered = new Set<string>();

/** The bundled open fonts and the registered ones are real fonts to embed, not standard ones. */
export const isStandardFont = (family: string): boolean => !isBundledFamily(family) && !registered.has(family) && themeIsStandard(family);

/** Makes the browser draw a font (online or bundled) so text boxes and the editor show it. */
export async function showFont(family: string): Promise<void> {
    if (isBundledFamily(family)) return showBundled(family);
    return themeShowFont(family);
}

const programs = new Map<string, Promise<Uint8Array | null>>();

const styleOf = (bold: boolean, italic: boolean) => `${bold ? "b" : ""}${italic ? "i" : ""}` || "r";
export const fontKey = (family: string, bold: boolean, italic: boolean) => `${family}|${styleOf(bold, italic)}`;

async function fetchOnline(o: OnlineFont, bold: boolean, italic: boolean): Promise<Uint8Array | null> {
    const get = async (b: boolean, i: boolean) => {
        const r = await fetch(`${FONT_CDN}/${o.id}@latest/latin-${b ? 700 : 400}-${i ? "italic" : "normal"}.woff`);
        return r.ok ? woffToSfnt(new Uint8Array(await r.arrayBuffer())) : null;
    };
    return (await get(bold, italic && o.italic).catch(() => null)) ?? (await get(false, false).catch(() => null));
}

export async function fetchLocal(family: string, bold: boolean, italic: boolean): Promise<Uint8Array | null> {
    const list = localFontRecords(family);
    if (!list?.length) return null;
    const score = (f: LocalFontData) => {
        const s = f.style.toLowerCase();
        return (/bold|black|heavy/.test(s) === bold ? 2 : 0) + (/italic|oblique/.test(s) === italic ? 2 : 0) + (/condensed|narrow|light|thin|medium/.test(s) ? 0 : 1);
    };
    const best = [...list].sort((a, b) => score(b) - score(a))[0]!;
    const bytes = new Uint8Array(await (await best.blob()).arrayBuffer());
    // OpenType with CFF outlines ("OTTO") can't be embedded as TrueType; the box then falls back to a standard font in the file.
    return String.fromCharCode(bytes[0]!, bytes[1]!, bytes[2]!, bytes[3]!) === "OTTO" ? null : bytes;
}

/** The TrueType font program for a chosen font, or null for a standard font (nothing to embed) or when it can't be had. */
export function fontProgram(family: string, bold: boolean, italic: boolean): Promise<Uint8Array | null> {
    const key = fontKey(family, bold, italic);
    let p = programs.get(key);
    if (!p) {
        const o = onlineFont(family);
        p = isBundledFamily(family) ? loadBundled(family, bold, italic)
            : o ? fetchOnline(o, bold, italic)
            : !isStandardFont(family) ? fetchLocal(family, bold, italic).then((b) => b ?? loadGoogle(family, bold, italic))
            : Promise.resolve(null);
        programs.set(key, p);
    }
    return p;
}

/** Makes a font program known under a family name: the editor draws text in it and the saved file embeds it. */
export async function registerProgramFamily(family: string, bold: boolean, italic: boolean, sfnt: Uint8Array): Promise<void> {
    registered.add(family);
    programs.set(fontKey(family, bold, italic), Promise.resolve(sfnt));
    if (typeof FontFace === "undefined") return;
    try {
        const f = new FontFace(family, sfnt.slice().buffer, { weight: bold ? "700" : "400", style: italic ? "italic" : "normal" });
        document.fonts.add(await f.load());
    } catch { /* the fallback family draws instead */ }
}
